/* eslint-disable no-shadow */
import { getInitialState } from '@/components/commons/ServerSideRendererUtils.js';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage.js';

const initialState = getInitialState() || {};
const search = initialState?.common?.search ?? {
  app: process.env.ALGOLIA_APP_ID,
  key: process.env.ALGOLIA_API_KEY,
};

let analytics = null;

async function getInstance() {
  if (analytics) {
    return analytics;
  }
  analytics = (await import(/* wepbackChunkName: "SearchInsights" */ 'search-insights')).default;
  analytics.init({
    appId: search.app,
    apiKey: search.key,
  });
  return analytics;
}

/**
 * Queue up a product click track event from a search page.
 *
 * @param {String} index The name of the Algolia index used for search
 * @param {Number} productId The id of the product clicked
 * @param {String} queryId The query id from which the product was clicked
 * @param {Number} position The position/index in the search results that the product appeared
 */
export async function queueProductClick(index, productId, queryId, position) {
  try {
    if (index && productId && queryId && (position || position === 0)) {
      const positionAdjusted = position + 1;
      LocalStorage.set(
        LocalStorageKeys.SEARCH_PRODUCT_CLICK,
        JSON.stringify({
          index,
          queryId,
          productId,
          position: positionAdjusted,
          tracked: false,
        }),
      );
    } else {
      LocalStorage.set(LocalStorageKeys.SEARCH_PRODUCT_CLICK, null);
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Send a queued up product click event if the given product id matches the queued product id.
 *
 * @param {Number} productId The id of the product clicked
 */
export async function trackProductClick(productId) {
  try {
    const productClicked = JSON.parse(LocalStorage.get(LocalStorageKeys.SEARCH_PRODUCT_CLICK));
    if (productId && productClicked && productClicked.productId && productClicked.productId === productId && !productClicked.tracked) {
      const analytics = await getInstance();
      analytics.clickedObjectIDsAfterSearch({
        index: productClicked.index,
        eventName: 'View product',
        objectIDs: [`${productId}`],
        queryID: productClicked.queryId,
        positions: [productClicked.position],
      });
      LocalStorage.set(LocalStorageKeys.SEARCH_PRODUCT_CLICK, JSON.stringify(Object.assign(productClicked, { tracked: true })));
    }
  } catch (e) {
    console.error(e);
  }
}

/**
 * Track a product added to the cart from search results.
 *
 * @param {Number} productId The id of the product that was added to the cart
 */
export async function trackProductAddToCart(productId) {
  try {
    const productClicked = JSON.parse(LocalStorage.get(LocalStorageKeys.SEARCH_PRODUCT_CLICK));
    if (productId && productClicked && productClicked.productId && parseInt(productClicked.productId) === productId) {
      const analytics = await getInstance();
      analytics.convertedObjectIDsAfterSearch({
        index: productClicked.index,
        eventName: 'Add to cart',
        objectIDs: [`${productId}`],
        queryID: productClicked.queryId,
      });
    }
    LocalStorage.set(LocalStorageKeys.SEARCH_PRODUCT_CLICK, null);
  } catch (e) {
    console.error(e);
  }
}
